/**
 * Javascript file for the page to create the company profile.
 * @author Adrian Rebmann, Eduardo Parducci
 */

import {
  getCompanyProfile,
  getTechnologies,
  postCompany
} from "../../../../api";
import {
  required,
  email,
  minLength,
  maxLength,
  between
} from "vuelidate/lib/validators";

export default {
  name: "ProfileCreate",

  /**
   * The vue instance's data object.
   * @property {boolean} loading - Information is currently being loaded.
   * @property {object} profile - The profile created.
   * @property {string[]} form.companysizes - Options for the company's size.
   * @property {string[]} form.revenues - Options for the company's revenue.
   * @property {string[]} form.sectors - Options for the company's sector.
   * @property {boolean} saved - Information has been saved.
   * @property {boolean} sending - Information is currently being saved.
   * @property {string} searchtermint - Search term for demanded technologies.
   * @property {string} searchtermexp - Search term for offered technologies.
   * @property {object[]} searchresultsint - Search results for demanded technologies.
   * @property {object[]} searchresultsexp - Search results for offered technologies.
   * @property {object[]} technologies - Result set for technologies from the backend query.
   * @property {boolean} unsavedChanges - Activate the "there are still unsaved changes" pop-up.
   * @property {object} searchoptions - Search parameters for VueFuse. For more details, check: https://www.npmjs.com/package/vue-fuse/v/1.5.2
   * @property {string[]} searchoptions.keys - The key values for the search.
   */
  data() {
    return {
      loading: false,
      profile: null,
      form: {
        companysizes: ["1 - 10", "10 - 50", "50 - 500", "500 - 2000", "> 2000"],
        revenues: ["< 2 Mio.", "2 - 10 Mio.", "10 - 50 Mio.", "> 50 Mio."],
        sectors: [
          "Banken, Versicherungen, Finanzdienstleistungen",
          "Baugewerbe",
          "Unternehmensberatung",
          "Bergbau und fossile Stoffe",
          "Bildung und Soziales",
          "Chemieindustrie",
          "Elektroindustrie",
          "Energie",
          "Freizeit, Kunst, Kultur",
          "Gesundheit und Pflege",
          "Handel",
          "Handwerk",
          "Haushaltsnahe Dienstleistungen",
          "Immobilien",
          "IT und Telekommunikation",
          "Land-und Forstwirtschaft, Fischerei",
          "Maschinen- und Anlagenbau",
          "Medien",
          "Medizintechnik",
          "Metallindustrie",
          "Pharmaindustrie",
          "Tourismus, Gastronomie",
          "Umwelt- und Energietechnik",
          "Verkehr, Logistik, Lagerei"
        ]
      },
      saved: false,
      sending: false,
      searchtermint: "",
      searchtermexp: "",
      searchresultsint: [],
      searchresultsexp: [],
      technologies: [],
      unsavedChanges: false,
      searchoptions: {
        shouldSort: true,
        threshold: 0.6,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: ["name", "description"]
      }
    };
  },

  /**
   * Validation to be done to the data.
   * @property {*} profile.name - The name of the company.
   * @property {*} profile.description - The description of the company.
   * @property {*} profile.sector - The sector of the company.
   * @property {*} profile.city - The city the company is located in.
   * @property {*} profile.zip - The zip code of the city the company is located in.
   * @property {*} profile.street - The street the company is located in.
   * @property {*} profile.email - The email to contact the company.
   * @property {*} profile.size - The size (number of employees) of the company.
   * @property {*} profile.founding - The year the company was founded.
   */
  validations: {
    profile: {
      name: {
        required,
        minLength: minLength(3)
      },
      description: {
        required,
        minLength: minLength(20)
      },
      sector: {
        required
      },
      city: { required },
      zip: { required, minLength: minLength(5), maxLength: maxLength(5) },
      street: { required },
      contact: { required },
      //phone: { required },
      email: { required, email },
      size: { required },
      founding: { required, between: between(1500, 2030) }
    }
  },

  /**
   * Called before the instance is destroyed.
   */
  beforeDestroy() {
    //localStorage.setItem("profile", JSON.stringify(this.profile));
  },

  /**
   * Called after the instance has been created.
   */
  created() {
    this.loading = true;
    this.technologies = [];
    this.searchresultsexp = [];
    this.searchresultsint = [];
    getTechnologies().then(res => {
      this.technologies = res;

      //this.searchresultsint = this.technologies;
      //this.searchresultsexp = this.technologies;
      getCompanyProfile().then(res2 => {
        console.log("Initial Load" + JSON.stringify(res2));
        this.profile = res2;
        //TODO: Remove this
        //if (localStorage.getItem("profile") !== null && JSON.stringify(this.profile != localStorage.getItem("profile"))) {
        // this.profile = JSON.parse(localStorage.getItem("profile"));
        // this.unsavedChanges = true;
        //}
        this.loading = false;
      });
    });
  },

  /**
   * The computed properties to be mixed into the Vue instance.
   */
  computed: {
    messageClass() {
      return {
        "md-invalid": this.hasMessages
      };
    }
  },

  /**
   * Expressions to watch.
   */
  watch: {
    searchtermint: function() {
      this.searchInt();
    },
    searchtermexp: function() {
      this.searchExp();
    }
  },

  /**
   * The vue instance's method declaration.
   */
  methods: {
    /**
     * Enables the view of the offered technologies available and sorts them alphabetically.
     */
    switchExp() {
      this.searchresultsexp = this.technologies;
      this.searchresultsexp.sort(function(a, b) {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },

    /**
     * Enables the view of the demanded technologies and sorts them alphabetically.
     */
    switchInt() {
      this.searchresultsint = this.technologies;
      this.searchresultsint.sort(function(a, b) {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },

    /**
     * Adds tech to the list of selected offered technologies.
     * @param {*} tech - The selected technology.
     */
    addToInt(tech) {
      for (let i = 0; i < this.profile.requested.length; i++) {
        if (this.profile.requested[i].hash == tech.hash) {
          return;
        }
      }
      this.profile.requested.push(tech);
    },

    /**
     * Adds tech to the list of demanded technologies.
     * @param {*} tech - The selected technology.
     * @returns - Returns from the function.
     */
    addToExp(tech) {
      for (let i = 0; i < this.profile.offered.length; i++) {
        if (this.profile.offered[i].hash == tech.hash) {
          return;
        }
      }
      this.profile.offered.push(tech);
    },

    /**
     * Removes tech from the list of offered technologies.
     * @param {*} tech - The selected technology.
     */
    removeFromInt(tech) {
      let toRemove = -1;
      for (let i = 0; i < this.profile.requested.length; i++) {
        if (this.profile.requested[i].hash == tech.hash) {
          toRemove = i;
        }
      }
      this.profile.requested.splice(toRemove, 1);
    },

    /**
     * Removes tech from the list of demanded technologies.
     * @param {*} tech - The selected technology.
     */
    removeFromExp(tech) {
      let toRemove = -1;
      for (let i = 0; i < this.profile.offered.length; i++) {
        if (this.profile.offered[i].hash == tech.hash) {
          toRemove = i;
        }
      }
      this.profile.offered.splice(toRemove, 1);
    },

    /**
     * Searches for matching offered technologies.
     */
    searchInt() {
      this.$search(
        this.searchtermint,
        this.technologies,
        this.searchoptions
      ).then(results => {
        this.searchresultsint = results.splice(0, 20);
      });
    },

    /**
     * Searches for matching demanded technologies.
     */
    searchExp() {
      this.$search(
        this.searchtermexp,
        this.technologies,
        this.searchoptions
      ).then(results => {
        this.searchresultsexp = results.splice(0, 20);
      });
    },

    /**
     * Determines if the field is invalid or not.
     * @param {*} fieldName - The name of the field.
     * @returns - Status of validity of the field.
     */
    getValidationClass(fieldName) {
      const field = this.$v.profile[fieldName];
      if (field) {
        return {
          "md-invalid": field.$invalid && field.$dirty
        };
      }
    },

    /**
     * Resets the form.
     */
    clearForm() {
      this.$v.$reset();
    },

    /**
     * Checks if the form's sector includes the profile's sector and resets the profile's sector if that is not the case.
     */
    checkSector() {
      if (!this.form.sectors.includes(this.profile.sector)) {
        this.profile.sector = "";
      }
    },

    /**
     * Saves the profile in the backend.
     */
    saveProfile() {
      this.sending = true;
      postCompany(this.profile).then(res => {
        //TODO: Ask Kai about this!
        console.log("Res after sending" + JSON.stringify(res));
        this.saved = true;
        this.sending = false;
        this.profile = res;
        // this.searchtermint = res.
        this.unsavedChanges = false;
        //localStorage.removeItem("profile");
      });
    },

    /**
     * Validates the profile.
     * If the profile is valid, it saves the profile.
     */
    validateProfile() {
      this.checkSector();
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.saveProfile();
      }
    },

    /**
     * Returns to the previously visited page.
     */
    goBack() {
      this.$router.go(-1);
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/company/profile/create/profilecreate.js